<template>
  <div class="credentials-list">
    <template v-if="(invalidCredentialsList.length) > 0">
      <div class="creds-card-wrapper" v-for="(source, index) in invalidCredentialsList" :key="source.credentialsId">
        <discount-waiting-credentials-card v-if="!canMigrateToOBK(source) && isDiscountApi(source.sourceName)" :source="source"/>
        <open-banking-renewal-credentials-card v-else-if="isOpenBanking(source)" :source="source"/>
        <invalid-crednetials-card v-else :key="index"
                                  class="account-wrapper"
                                  :source="source"
                                  :migration-from-passwords="migrationFromPasswords"
                                  @update-credentials="() => openUpdateCredentialsFlow(source)"
        />
      </div>
    </template>

    <template v-if="connectedCredentials.length > 0">
      <connected-credentials-card v-for="source in bankAccounts" :key="source.credentialsId"
                                  class="account-wrapper"
                                  :source="source"
                                  :source-type="source.type"
                                  :name="source.name"
                                  :sourceName="source.sourceName"
                                  :status="source.status"
                                  :accounts="source.accounts"
                                  :enable-menu="true">
        <template v-slot:buttons>
          <partially-authorized-retry-scrape-button v-if="source.status === 'partiallyAuthorized'"/>
          <riseup-button v-if="canMigrateToOBK(source)"
                         class="migrate-obk-btn"
                         size="slim" variant="secondary" title="להחליף לחיבור ישיר" :action="() => migrateToOBK(source)" />
        </template>
      </connected-credentials-card>
      <separator class="accounts-separator" v-if="creditCardAccounts.length > 0"/>
      <connected-credentials-card v-for="source in creditCardAccounts" :key="source.credentialsId"
                                  class="account-wrapper"
                                  :source="source"
                                  :source-type="source.type"
                                  :name="source.name"
                                  :sourceName="source.sourceName"
                                  :status="source.status"
                                  :accounts="source.accounts"
                                  :enable-menu="true">
        <template v-slot:buttons>
          <riseup-button v-if="canMigrateToOBK(source)"
                         class="migrate-obk-btn"
                         size="slim" variant="secondary" title="להחליף לחיבור ישיר" :action="() => migrateToOBK(source)" />
        </template>
      </connected-credentials-card>
    </template>
    <div v-if="invalidCredentialsList.length === 0">
      <separator class="separator"/>
      <add-credentials-card
          class="new-account-wrapper"
          title="להוספת חשבון בנק או אשראי"
          :add-new-credentials="openCreateCredentialsFlow"/>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapGetters, mapActions } from 'vuex';
import BaseUI from 'base-ui';
import PartiallyAuthorizedRetryScrapeButton
  from '@/base-components/credentials/credentials-card/open-banking/PartiallyAuthorizedRetryScrapeButton';
import ConnectionTypeChoicePopup from '@/base-components/credentials/credentials-list/ConnectionTypeChoicePopup';
import OpenBankingCelebrationPopup
  from '@/pages/responsive-pages/non-authenticated/open-banking/OpenBankingCelebrationPopup';
import BankCCWarningPopup from '@/base-components/credentials/credentials-list/BankCCWarningPopup';
import Segment from '@/Segment';
import OpenBankingRenewalCredentialsCard from '../credentials-card/open-banking/OpenBankingRenewalCredentialsCard.vue';
import DiscountWaitingCredentialsCard from '../credentials-card/discount/WaitingCredentialsCard.vue';
import discountSources from '../../../constants/discount-sources';
import ConnectedCredentialsCard from '../../onboarding/ConnectedCredentialsCard';
import AddCredentialsCard from '../../onboarding/AddCredentialsCard';
import AddCredentialsFlow
  from '../../../pages/responsive-pages/authenticated/navigation-pages/credentials-settings/add-credentials-flow/AddCredentialsFlow';
import InvalidCrednetialsCard from './InvalidCrednetialsCard';

export default {
  name: 'CredentialsList',
  components: {
    InvalidCrednetialsCard,
    DiscountWaitingCredentialsCard,
    ConnectedCredentialsCard,
    AddCredentialsCard,
    PartiallyAuthorizedRetryScrapeButton,
    OpenBankingRenewalCredentialsCard,
    Separator: BaseUI.Separator,
    RiseupButton: BaseUI.RiseupButton,
  },
  computed: {
    ...mapState('credentials', ['credentialsList', 'credsToAccounts']),
    ...mapGetters('credentials', ['invalidCredentialsList', 'connectedCredentials',
      'validCredentialsList', 'unvalidatedCredentialsList', 'partiallyAuthorizedCredentialsList', 'getCredsBankCCAccounts']),
    ...mapGetters('featureFlags', ['migrationFromPasswords']),
    bankAccounts() {
      return _.filter(this.connectedCredentials, c => c.type === 'bank');
    },
    creditCardAccounts() {
      return _.filter(this.connectedCredentials, c => c.type === 'card');
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal']),
    isDiscountApi(sourceName) {
      return sourceName === discountSources.DISCOUNT_API;
    },
    canMigrateToOBK(credentials) {
      if (!this.migrationFromPasswords || this.isOpenBanking(credentials)) {
        return false;
      }
      const accountsWithoutBankCC = this.getCredsAccountsWithoutBankCC(credentials);
      return !this.accountsExistsInOBKCredentials(accountsWithoutBankCC, credentials.credentialsId);
    },
    accountsExistsInOBKCredentials(accounts, credentialsId) {
      const otherCredentialsSortedAccountsNumbers = _.chain(this.credentialsList)
        .reject(a => a.credentialsId === credentialsId)
        .filter(a => this.isOpenBanking(a))
        .flatMap(a => a.accounts)
        .compact()
        .map(pii => pii.accountNumberPiiValue)
        .value();
      // checking if accounts from other credentials conatins the accounts from current credentials
      return _.intersection(otherCredentialsSortedAccountsNumbers, accounts).length === accounts.length;
    },
    getCredsAccountsWithoutBankCC(credentials) {
      const credsAccountWithoutBankCC = credentials.type === 'bank'
        ? _.reject(credentials.accounts, a => a.sourceType === 'card')
        : credentials.accounts;
      return _.chain(credsAccountWithoutBankCC)
        .map(pii => pii.accountNumberPiiValue).sortBy()
        .value();
    },
    isOpenBanking(source) {
      return !!source.openBankingConsentId;
    },
    openCreateCredentialsFlow() {
      this.openModal({
        component: AddCredentialsFlow,
        popupAlignment: 'full-screen',
      });
    },
    openUpdateCredentialsFlow(source) {
      Segment.trackUserInteraction('UpdateInvalidCreds_Clicked', {
        sourceName: source.sourceName,
        migrationFromPasswords: this.migrationFromPasswords,
      });
      if (this.migrationFromPasswords) {
        this.openModal({
          component: ConnectionTypeChoicePopup,
          props: {
            source,
            connectWithOBK: () => this.connectWithOBK(source),
          },
        });
      } else {
        this.openModal({
          component: AddCredentialsFlow,
          props: {
            sourceToUpdate: source,
            updateMode: true,
          },
          popupAlignment: 'full-screen',
        });
      }
    },
    migrateToOBK(source) {
      Segment.trackUserInteraction('MigrateToOBKClicked', { sourceName: source.sourceName });
      this.connectWithOBK(source);
    },
    connectWithOBK(source) {
      this.openModal({
        component: OpenBankingCelebrationPopup,
        props: {
          source,
          action: () => {
            const bankCCAccounts = this.getCredsBankCCAccounts(source.credentialsId);
            if (bankCCAccounts?.length) {
              this.openBankCCWarningPopup(source, bankCCAccounts);
            } else {
              this.openOBKConnectionFlow(source);
            }
          },
        },
      });
    },
    openBankCCWarningPopup(source, bankCCAccounts) {
      this.openModal({
        component: BankCCWarningPopup,
        props: {
          source,
          bankCCAccounts,
          openOBKConnectionFlow: () => this.openOBKConnectionFlow(source),
        },
      });
    },
    openOBKConnectionFlow(source) {
      this.openModal({
        component: AddCredentialsFlow,
        props: {
          sourceNameToAdd: source.sourceName,
          closedBankingCredentialsId: source.credentialsId,
          preventLoadingCredentialsCheck: true,
        },
        popupAlignment: 'full-screen',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../node_modules/base-ui/src/scss/riseup-colors';
@import '../../../scss/mixins';

.credentials-list {
  @include responsive-padding;
  width: 100%;
  padding-top: 40px;

  .title {
    @include title;
    margin-bottom: 10px;
  }

  .separator {
    margin-top: 40px;
  }

  .accounts-separator {
    margin-top: 0;
  }

  .accounts-separator, .account-wrapper {
    margin-bottom: 24px;
  }

  .add-new, .creds-card-wrapper {
    margin-bottom: 16px;
  }

  .new-account-wrapper {
    margin: 40px 0;
  }

  .migrate-obk-btn{
    margin-top: 30px;
  }
}
</style>
